<template>
  <el-table-column
    :prop="prop"
    :width="width"
    :min-width="minWidth"
    :sortable="sortableValue"
    :sort-by="sortField"
    :align="align"
    :fixed="fixed"
    label-class-name="table-header"
    :show-overflow-tooltip="showOverflowTooltip"
  >
    <template slot="header" slot-scope="{}">
      <span :title="title">{{ title }}</span>
      <div class="RestList--Tooltip" v-if="tooltipText && tooltipText.length">
        <el-tooltip
          class="RestList--TooltipContent"
          effect="light"
          :content="tooltipText"
          placement="bottom"
        >
          <i class="el-icon-info"></i>
        </el-tooltip>
      </div>
      <div v-if="filterable && restList.filterable" @click.stop>
        <rest-list-filter-select
          v-if="filterOptions"
          :prop="filterField || prop"
          :options="filterOptions"
          :order="filterOptionsOrder"
          :allow-multiple="allowMultiple"
          :clearable="clearable"
          :searchable="searchable"
          :default-value="filters && filters[prop] ? filters[prop] : null"
          :data-qa="`filter-${(prop || '').replace(/_/g, '-')}-dropdown`"
        />

        <rest-list-filter-date-range
          v-else-if="filterDateRange"
          :prop="filterField || prop"
          :readonly="!filterable"
          :date-picker-props="datePickerProps"
          :default-value="filters && filters[prop] ? filters[prop] : null"
          :convert-to-utc="convertToUtc"
        />

        <rest-list-filter-date
          v-else-if="filterDate"
          :prop="filterField || prop"
          :readonly="!filterable"
          :date-picker-props="datePickerProps"
          :default-value="filters && filters[prop] ? filters[prop] : ''"
        />

        <rest-list-filter-number
          v-else-if="filterNumber"
          :prop="filterField || prop"
          :readonly="!filterable"
          :default-value="filters && filters[prop] ? filters[prop] : ''"
        />

        <rest-list-filter-input
          v-else-if="filterInput"
          :prop="filterField || prop"
          :readonly="!filterable"
          :default-value="filters && filters[prop] ? filters[prop] : null"
          :filter-on-enter="filterOnEnter"
          :data-qa="`filter-${(prop || '').replace(/_/g, '-')}-input`"
          :min-chars="minChars"
          :debounce-time="debounceTime"
        />
      </div>
    </template>

    <template v-slot:default="scope">
      <rest-list-column-editable
        v-if="editable && isEdit(scope)"
        :model="editData(scope)"
        :prop="prop"
        :disabled="disabled"
        :data-qa="`edit-${prop.replace('_', '-')}-input`"
      >
        <template v-slot>
          <slot name="input-edit" :model="editData(scope)" />
        </template>
      </rest-list-column-editable>
      <slot
        v-else
        name="default"
        v-bind="scope"
        :scope="scope"
        :rowValue="rowValue"
        :rowRoute="rowRoute"
      >
        <router-link
          v-if="rowRoute(scope.row)"
          :to="rowRoute(scope.row)"
          :target="newTab ? '_blank' : '_self'"
          data-qa="row-link-button"
        >
          {{ rowValue(scope) }}
        </router-link>

        <overflow-tooltip v-else-if="tooltip" :value="rowValue(scope)">
          <span>
            {{ rowValue(scope) }}
          </span>
        </overflow-tooltip>

        <metric
          v-else-if="propIsMetric"
          :metric="rowValue(scope).value"
          :type="rowValue(scope).type"
        />

        <span v-else :data-qa="`cell-${prop.replace(/_/g, '-')}`">
          {{ rowValue(scope) }}
        </span>

        <el-tag
          style="margin-left: 10px"
          v-if="rowLabel(scope)"
          size="small"
          :type="rowLabel(scope).type"
        >
          {{ rowLabel(scope).label }}
        </el-tag>
      </slot>
    </template>
  </el-table-column>
</template>

<script>
import RestListFilterInput from '@/common/components/rest/RestListFilterInput'
import RestListFilterSelect from '@/common/components/rest/RestListFilterSelect'
import RestListFilterDate from '@/common/components/rest/RestListFilterDate'
import RestListFilterDateRange from '@/common/components/rest/RestListFilterDateRange'
import RestListFilterNumber from '@/common/components/rest/RestListFilterNumber'
import Metric from '@/common/components/Metric'
import _ from 'lodash'
import OverflowTooltip from '@/common/components/form/OverflowTooltip'
import RestListColumnEditable from '@/common/components/rest/RestListColumnEditable'

export default {
  name: 'RestListColumn',

  inject: {
    restList: 'restList',
    isEditingRow: { default: () => false },
    editRowData: { default: () => null }
  },

  components: {
    RestListColumnEditable,
    OverflowTooltip,
    RestListFilterSelect,
    RestListFilterInput,
    RestListFilterDate,
    RestListFilterDateRange,
    RestListFilterNumber,
    Metric
  },

  props: {
    prop: {
      type: String,
      required: true
    },

    title: {
      type: String,
      required: true
    },

    route: {
      type: Function,
      required: false
    },
    newTab: {
      type: Boolean,
      required: false
    },

    formatter: {
      type: Function,
      required: false,
      default: value => {
        if (value === null || value === undefined) {
          return '-'
        } else {
          return value
        }
      }
    },

    filterable: {
      type: Boolean,
      default: true
    },

    filterField: {
      type: String,
      required: false
    },

    filterOptions: {
      required: false
    },

    filterOptionsOrder: {
      required: false
    },

    filterDateRange: {
      type: Boolean,
      required: false
    },

    filterDate: {
      type: Boolean,
      required: false
    },

    filterInput: {
      type: Boolean,
      required: false,
      default: true
    },

    filterNumber: {
      type: Boolean,
      default: false
    },

    fixed: {
      type: [Boolean, String],
      required: false,
      default: false
    },

    width: {
      type: Number,
      required: false
    },

    minWidth: {
      type: Number,
      required: false
    },

    datePickerProps: {
      type: Object,
      required: false
    },

    // Scope: when true, date range filter will convert to UTC before sending
    convertToUtc: {
      type: Boolean,
      default: false
    },

    sortable: {
      type: Boolean,
      default: true
    },

    sortField: {
      type: String,
      required: false
    },

    tooltip: {
      type: Boolean,
      default: false
    },

    propIsMetric: {
      type: Boolean,
      default: false
    },

    editable: {
      type: Boolean,
      default: false
    },

    align: {
      type: String,
      required: false
    },

    allowMultiple: {
      type: Boolean,
      default: false
    },

    clearable: {
      type: Boolean,
      default: true
    },

    disabled: {
      type: Boolean,
      default: false
    },

    showOverflowTooltip: {
      type: Boolean,
      required: false,
      default: false
    },

    tooltipText: {
      type: String,
      required: false
    },

    filterOnEnter: {
      type: Boolean,
      default: false
    },

    label: {
      type: Function,
      required: false
    },

    debounceTime: {
      type: Number,
      default: 500
    },

    minChars: {
      type: Number,
      default: 0
    },

    searchable: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      filters: null
    }
  },

  mounted() {
    if (this.$route.query['filters']) {
      this.filters = JSON.parse(this.$route.query['filters'])
    }
  },

  computed: {
    sortableValue() {
      return this.sortable ? 'custom' : false
    }
  },

  methods: {
    isEdit(scope) {
      return this.isEditingRow(scope.$index)
    },

    editData(scope) {
      return this.editRowData(scope.$index)
    },

    rowRoute(row) {
      if (this.route) {
        return this.route(row)
      } else {
        return null
      }
    },

    rowValue(scope) {
      return this.formatter(_.get(scope.row, this.prop), scope.row)
    },

    rowLabel(scope) {
      if (this.label) {
        return this.label(scope.row)
      }

      return false
    }
  }
}
</script>
<style scoped>
>>> p {
  font-weight: normal !important;
  font-size: unset !important;
  line-height: unset !important;
  color: unset !important;
}

.RestList--Tooltip {
  position: absolute;
  right: 12px;
  top: 0;
}
</style>



// WEBPACK FOOTER //
// src/common/components/rest/RestListColumn.vue